import {ApolloError, FetchResult} from '@apollo/client'
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo
} from 'react'
import createReducer from 'react-use/lib/factory/createReducer'

import {getInitialUndoRedoState, State, UndoRedoState} from '.'
import {
  SaveConfigMutation,
  SaveConfigMutationOptions
} from '../hooks/generated/save-config'
import {useSaveConfig} from '../hooks/save-config'
import {Action} from './actions'
import {ThunkDispatch, thunkMiddleware} from './middleware/thunk'
import {reducer} from './reducer'
import {undoRedoFlowReducer} from './undoableReducer'

// import reduxLogger from 'redux-logger'
export type AppDispatch = ThunkDispatch<UndoRedoState, Action>
export type ThunkAction = (
  dispatch: AppDispatch,
  getState: () => UndoRedoState
) => void

export type SaveConfig = (
  options?: SaveConfigMutationOptions
) => Promise<FetchResult<SaveConfigMutation>>

type Context = {
  state: State
  dispatch: AppDispatch
  save: SaveConfig
}

export type QueryState = {loading: boolean; error: ApolloError | undefined}

export const StateContext = createContext<Context | undefined>(undefined)

const enhancedReducer = createReducer<Action, UndoRedoState>(
  thunkMiddleware
  // we can use any redux middleware here due to createReduce providing the same middleware interface
  // the redux logger is nice while we are developing this I think
  // @ts-ignore although TS isn't quite happy, probably solvable
  // reduxLogger
)

export const useActionFlowReducer = (
  initialState: State
): {
  state: State
  dispatch: AppDispatch
  saveConfig: SaveConfig
  saveStatus: QueryState
  enableUndo: boolean
  enableRedo: boolean
} => {
  const {save, saveDebounced, loading, error} = useSaveConfig()

  const wrappedReducer = useCallback(
    (state: UndoRedoState, action: Action) => {
      const result = undoRedoFlowReducer(reducer)(state, action)

      // trigger autosave (debounced) whenever the flow document changes.
      if (
        'type' in action &&
        result.present.flow !== state.present.flow &&
        action.type !== 'reset-state' // if we've reset the state to match the server, don't save it back to the server
      ) {
        saveDebounced(result.present.flow)
      }

      return result
    },
    [saveDebounced]
  )
  const [state, dispatch] = enhancedReducer(
    wrappedReducer,
    getInitialUndoRedoState(initialState)
  )

  const saveConfig = useCallback<SaveConfig>(
    options =>
      new Promise<FetchResult<SaveConfigMutation>>((resolve, reject) => {
        dispatch((_, getState) => {
          save(getState().present.flow, options).then(resolve).catch(reject)
        })
      }),
    [dispatch, save]
  )

  const saveStatus = useMemo(() => ({loading, error}), [loading, error])

  return {
    state: state.present,
    dispatch,
    saveConfig,
    saveStatus,
    enableUndo: !!state.past.length,
    enableRedo: !!state.future.length
  }
}

type StateProviderProps = {
  children: ReactNode
  state: State
  dispatch: AppDispatch
  save: SaveConfig
}
export const StateProvider = ({
  children,
  state,
  dispatch,
  save
}: StateProviderProps) => {
  const value = useMemo<Context>(
    () => ({
      state,
      dispatch,
      save
    }),
    [state, dispatch, save]
  )

  return <StateContext.Provider value={value}>{children}</StateContext.Provider>
}

export const useAFSave = () => {
  const context = useContext(StateContext)
  if (!context) {
    throw new Error('Using state outside of StateProvider')
  }
  return context.save
}

export const useAFDispatch = () => {
  const context = useContext(StateContext)
  if (!context) {
    throw new Error('Using state outside of StateProvider')
  }
  return context.dispatch
}

// Use this with caution, config will change often causing your component to re-render
export const useAFConfig = () => {
  const context = useContext(StateContext)
  if (!context) {
    throw new Error('Using state outside of StateProvider')
  }
  return context.state.flow.config
}
